<template>
  <DrawerModal
    :is-open="isOpen"
    title="profile.my.ratings.title"
    :has-changes="hasChanges"
    :is-loading="isLoading"
    :is-disabled="!hasChanges || !ratingStore.isRatingAdded() || !isRatingValid"
    @close="handleClose"
    @submit="saveRatings"
  >
    <gam-ratings class="ratings-profile" @initialized="captureInitialRatings" />
  </DrawerModal>
</template>

<script setup lang="ts">
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { useRatingStore } from '@/stores/rating.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamRatings from '@/views/components/gam-ratings/GamRatings.vue';
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

interface RatingsSectionProps {
  isOpen: boolean;
  onUpdate?: () => void;
}

const props = defineProps<RatingsSectionProps>();
const emit = defineEmits<{ close: []; update: [] }>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const ratingStore = useRatingStore();
const { ratings } = storeToRefs(ratingStore);
const isLoading = ref<boolean>(false);
const initialRatings = ref({});
const isInitialSnapshotReady = ref<boolean>(false);
const isDev = import.meta.env.DEV;

const isRatingValid = computed(() => true);

const handleClose = () => {
  if (isInitialSnapshotReady.value) {
    ratings.value = { ...initialRatings.value };
  }
  isInitialSnapshotReady.value = false;
  emit('close');
};

const captureInitialRatings = () => {
  if (isInitialSnapshotReady.value) return;

  initialRatings.value = { ...ratings.value };
  isInitialSnapshotReady.value = true;

  if (isDev) {
    // eslint-disable-next-line no-console
    console.log('[RatingsSection][dirty-check][baseline-captured]', {
      initialRatings: initialRatings.value,
      currentRatings: ratings.value,
      initialSerialized: JSON.stringify(initialRatings.value),
      currentSerialized: JSON.stringify(ratings.value),
    });
  }
};

const saveRatings = async () => {
  if (ratingStore.isRatingAdded() && userDetails.value) {
    ratingStore.clampGambitsRating();
    isLoading.value = true;
    const result = await gambits.ratingService.updateRatings({
      username: userDetails.value.username,
      firstName: userDetails.value.firstName || '',
      lastName: userDetails.value.lastName || '',
      birthDate: userDetails.value.birthDate,
      ...ratings.value,
    });

    await authStore.fetchUserInfo(true);
    isLoading.value = false;

    if (result.data) {
      emit('update');
      props.onUpdate?.();
      emit('close');
    } else {
      useToast().error(localize('profile.my.ratings.save_error'));
    }
  }
};

const hasChanges = computed(() => {
  if (!isInitialSnapshotReady.value) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[RatingsSection][dirty-check][awaiting-baseline]', {
        currentRatings: ratings.value,
        currentSerialized: JSON.stringify(ratings.value),
      });
    }

    return false;
  }

  const initialSerialized = JSON.stringify(initialRatings.value);
  const currentSerialized = JSON.stringify(ratings.value);
  const dirty = initialSerialized !== currentSerialized;

  if (isDev) {
    // eslint-disable-next-line no-console
    console.log('[RatingsSection][dirty-check][computed]', {
      initialSerialized,
      currentSerialized,
      dirty,
      initialRatings: initialRatings.value,
      currentRatings: ratings.value,
    });
  }

  return dirty;
});

watch(
  hasChanges,
  (nextDirty, prevDirty) => {
    if (!isDev) return;

    // eslint-disable-next-line no-console
    console.log('[RatingsSection][dirty-check][transition]', {
      prevDirty,
      nextDirty,
      currentRatings: ratings.value,
      initialRatings: initialRatings.value,
      currentSerialized: JSON.stringify(ratings.value),
      initialSerialized: JSON.stringify(initialRatings.value),
    });
  },
  { immediate: true },
);

onUnmounted(() => {
  ratingStore.reset();
});
</script>
